import React, { useState, useEffect } from "react";
import {
  Button,
  Grid,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { DeleteOutline } from "@mui/icons-material";
import CartItem from "./CartItem";
import CartSummary from "./CartSummary";
import {
  clearCart,
  getCartDetails,
  getIPAddress,
  deleteCartItem,
  updateCartItem,
} from "../../services/apiCalls";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { LoadingButton } from "@mui/lab";
import {
  clearCartThunk,
  removeItemFromCart,
  updateCartItemQuantity,
} from "../../features/cart/cartThunks";
import { useDispatch } from "react-redux";
import { useSnackbar } from "../../context/SnackbarContext";
import BreadCrumb from "../../components/BreadCrumb";

const CartPage = () => {
  const { showSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [loadingItems, setLoadingItems] = useState({});
  const [clearingCart, setClearingCart] = useState(false);
  const [ipAddress, setIpAddress] = useState(null);

  useEffect(() => {
    const fetchIp = async () => {
      try {
        const ip = await getIPAddress();
        setIpAddress(ip);
      } catch (error) {
        console.error("Failed to fetch IP address:", error);
      }
    };

    fetchIp();
    fetchCartItems();
  }, []);

  const fetchCartItems = async () => {
    try {
      setLoading(true);
      const response = await getCartDetails();
      if (!response.data) {
        setCartItems([]);
      } else {
        setCartItems(response.data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (cardId) => {
    try {
      setLoadingItems((prev) => ({ ...prev, [cardId]: true }));
      await dispatch(removeItemFromCart(cardId)).unwrap();
      setCartItems((prevItems) =>
        prevItems.filter((item) => item.card_id !== cardId)
      );
      showSnackbar("Item removed from cart", "success");
    } catch (error) {
      showSnackbar(error?.message || "Failed to remove item", "error");
    } finally {
      setLoadingItems((prev) => ({ ...prev, [cardId]: false }));
    }
  };

  const handleClearCart = async () => {
    try {
      setClearingCart(true);
      await dispatch(clearCartThunk()).unwrap();
      setCartItems([]);
      showSnackbar("Cart cleared successfully", "success");
    } catch (error) {
      showSnackbar(error?.message || "Failed to clear cart", "error");
    } finally {
      setClearingCart(false);
    }
  };

  const handleQuantityChange = async (item, newQuantity) => {
    try {
      setLoadingItems((prev) => ({ ...prev, [item.card_id]: true }));

      await updateCartItem({
        card_id: item.card_id,
        quantity: newQuantity.toString(),
        discount: item.discount,
      });

      setCartItems((prevItems) =>
        prevItems.map((cartItem) =>
          cartItem.card_id === item.card_id
            ? { ...cartItem, quantity: newQuantity }
            : cartItem
        )
      );

      showSnackbar("Quantity updated successfully", "success");
    } catch (error) {
      showSnackbar(error?.message || "Failed to update quantity", "error");
    } finally {
      setLoadingItems((prev) => ({ ...prev, [item.card_id]: false }));
    }
  };

  const breadcrumbUrls = [
    // { path: '/previous', label: 'Back' },
    { path: '/', label: 'Home' },
    { path: '/cart', label: 'Cart' },
  ];

  if (loading) {
    return (
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        sx={{ minHeight: "50vh" }}
      >
        <CircularProgress />
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container justifyContent="center" alignItems="center">
        <Typography color="error">{error}</Typography>
      </Grid>
    );
  }

  return (
    <div>
      <BreadCrumb title="Cart" urls={breadcrumbUrls} />
      <Grid container sx={{ width: "100%", mx: "auto", my: 3 }} spacing={2}>
        <Grid item xs={12} md={8} pr={{ xs: 0, md: 4 }}>
          <TableContainer
            component={Paper}
            sx={{
              display: { xs: "none", md: "block" },
              borderRadius: 0,
              mb: 4,
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              "&::-webkit-scrollbar": {
                height: "6px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#2189ff",
              },
            }}
          >
            <Table
              sx={{
                minWidth: { xs: 300, sm: 650, md: "100%" },
                "& .MuiTableCell-root": {
                  borderRadius: 0,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 100 }}>Product</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>Details</TableCell>
                  <TableCell sx={{ minWidth: 100 }}>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cartItems.map((item) => (
                  <TableRow key={item.card_id}>
                    <TableCell>
                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <img
                          src={item.product.images[0]}
                          alt={item.product.name}
                          style={{ width: 80, height: 80, objectFit: "cover" }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontSize: { xs: 16, sm: 20, md: 26 },
                            fontFamily: "'Playfair Display', serif",
                          }}
                        >
                          {item.product.name.length > 20
                            ? item.product.name.slice(0, 20) + "..."
                            : item.product.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.variant}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "'Nunito Sans', sans-serif",
                            fontSize: { xs: "0.9rem", sm: "1rem" },
                          }}
                        >
                          {item.product.currency} {parseFloat(item.unit_price).toFixed(2)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          mt: 2,
                          display: "flex",
                          justifyContent: "center",
                          gap: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "1px solid #ababab",
                            px: 1,
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleQuantityChange(item, parseFloat(item.quantity) - 1)
                            }
                            disabled={loadingItems[item.card_id] || item.quantity <= 1}
                            sx={{ color: "#000", "&:hover": { backgroundColor: "#e0e0e0" } }}
                          >
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <Typography
                            sx={{ mx: 2, textAlign: "center", fontSize: "1.25rem" }}
                          >
                            {loadingItems[item.card_id] ? (
                              <CircularProgress size={20} />
                            ) : (
                              item.quantity
                            )}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleQuantityChange(item, parseFloat(item.quantity) + 1)
                            }
                            disabled={loadingItems[item.card_id] || item.quantity >= item.stock}
                            sx={{ color: "#000", "&:hover": { backgroundColor: "#e0e0e0" } }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Button
                          variant="contained"
                          onClick={() => handleDeleteItem(item.card_id)}
                          disabled={loadingItems[item.card_id]}
                        >
                          {loadingItems[item.card_id] ? (
                            <CircularProgress size={20} />
                          ) : (
                            "Remove Item"
                          )}
                        </Button>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {item.product.currency}{" "}
                      {(parseFloat(item.unit_price) * parseFloat(item.quantity)).toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            sx={{ display: { xs: "block", md: "none" }, }}
          >
            {cartItems.map((item) => (
              <Box
                key={item.card_id}
                sx={{
                  mb: 2,
                  display: "flex",
                  flexDirection: "column",
                  border: "1px solid #ababab",
                }}
              >
                {/* Product Image */}
                <Box
                  py={2}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    borderBottom: "1px solid #ababab",
                  }}>
                  <img
                    src={item.product.images[0]}
                    alt={item.product.name}
                    style={{ width: 100, height: 100, objectFit: "cover" }}
                  />
                </Box>

                {/* Product Details */}
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontSize: 18,
                      fontFamily: "'Playfair Display', serif",
                    }}
                  >
                    {item.product.name.length > 20
                      ? `${item.product.name.slice(0, 20)}...`
                      : item.product.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.variant}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'Nunito Sans', sans-serif",
                      fontSize: "1rem",
                    }}
                  >
                    {item.product.currency}{" "}
                    {parseFloat(item.unit_price).toFixed(2)}
                  </Typography>
                </Box>

                {/* Actions */}
                <Box py={2} sx={{ display: "flex", justifyContent: "center", gap: 2, borderBottom: "1px solid #ababab", }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid #ababab",
                      px: 1,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleQuantityChange(
                          item,
                          parseFloat(item.quantity) - 1
                        )
                      }
                      disabled={parseFloat(item.quantity) <= 1}
                      sx={{ color: "#000", "&:hover": { backgroundColor: "#e0e0e0" } }}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <Typography sx={{ mx: 2 }}>{item.quantity}</Typography>
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleQuantityChange(
                          item,
                          parseFloat(item.quantity) + 1
                        )
                      }
                      disabled={parseFloat(item.quantity) >= item.stock}
                      sx={{ color: "#000", "&:hover": { backgroundColor: "#e0e0e0" } }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={() => handleDeleteItem(item.card_id)}
                    sx={{ px: 2 }}
                  >
                    Remove
                  </Button>
                </Box>

                {/* Total */}
                <Box py={2} sx={{ textAlign: "center", }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {item.product.currency}{" "}
                    {(
                      parseFloat(item.unit_price) * parseFloat(item.quantity)
                    ).toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Grid container justifyContent="space-between" spacing={2}>
            <Grid item>
              <Button variant="contained" href="/">
                Continue Shopping
              </Button>
            </Grid>
            <Grid item>
              <Button variant="contained" color="error" onClick={handleClearCart}>
                Clear Cart
              </Button>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={4}>
          <Grid sx={{ border: "1px solid #ababab" }}>
            <Grid
              item
              xs={12}
              sx={{
                fontWeight: 600,
                display: "flex",
                justifyContent: "center",
                borderBottom: "1px solid #ababab",
              }}
            >
              <Typography
                variant="h4"
                sx={{ fontSize: { xs: 20, md: 30 }, py: 1 }}
              >
                Cart Total
              </Typography>
            </Grid>
            <CartSummary
              total={cartItems.reduce(
                (sum, item) =>
                  sum + parseFloat(item.unit_price) * parseFloat(item.quantity),
                0
              )}
              currency={cartItems.length > 0 ? cartItems[0].product.currency : "$"}
            />
          </Grid>
          <Button
            variant="contained"
            fullWidth
            sx={{
              color: "#fff",
              py: 1.5,
              mt: 2,
              fontSize: { xs: "0.9rem", sm: "1rem" },
            }}
            href="/checkout"
          >
            Checkout
          </Button>
        </Grid>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Grid>
    </div>

  );
};

export default CartPage;
